import { useEffect } from 'react';
import { Modal, Form, Input, Alert, Typography } from 'antd';

const { Text } = Typography;

// Characters no common filesystem accepts in a file name.
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]+/g;

export function defaultExportFilename() {
    return `vellby-contacts-${new Date().toISOString().slice(0, 10)}`;
}

/**
 * Confirms a contacts CSV export: how many contacts go in, who is left out
 * for having no email (linked so an admin can go add one), and the file name.
 *
 *   <ExportCsvModal
 *       open={!!exportResult}
 *       result={exportResult}              // buildContactsCsv() output
 *       onCancel={() => setExportResult(null)}
 *       onExport={filename => …}           // "name.csv", already sanitised
 *   />
 */
function ExportCsvModal({ open, result, onCancel, onExport }) {
    const [form] = Form.useForm();

    useEffect(() => {
        if (open) form.setFieldsValue({ filename: defaultExportFilename() });
    }, [open, form]);

    const exported = result?.exported ?? 0;
    const skipped = result?.skippedEntities ?? [];

    const handleFinish = ({ filename }) => {
        const base = filename.trim().replace(/\.csv$/i, '').replace(INVALID_FILENAME_CHARS, '-');
        onExport(`${base || defaultExportFilename()}.csv`);
    };

    return (
        <Modal
            title="Export contacts as CSV"
            open={open}
            onCancel={onCancel}
            onOk={() => form.submit()}
            okText="Download"
            okButtonProps={{ disabled: exported === 0 }}
            destroyOnHidden
        >
            <Alert
                type={exported > 0 ? 'success' : 'error'}
                showIcon
                style={{ marginBottom: 12 }}
                title={exported > 0
                    ? `${exported} contact${exported === 1 ? '' : 's'} will be exported.`
                    : 'None of the selected people have an email, so there is nothing to export.'}
            />

            {skipped.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    title={`${skipped.length} selected ${skipped.length === 1 ? 'entity has' : 'entities have'} no email and will be left out`}
                    description={(
                        <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                            {skipped.map(e => (
                                <div key={e.id}>
                                    {/* New tab, so the table selection survives adding the email. */}
                                    <a href={`/entity/${e.id}`} target="_blank" rel="noopener noreferrer">{e.name || '(no name)'}</a>
                                </div>
                            ))}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Add an email on their page, then export again to include them.
                            </Text>
                        </div>
                    )}
                />
            )}

            <Form form={form} layout="vertical" onFinish={handleFinish} disabled={exported === 0}>
                <Form.Item
                    label="File name"
                    name="filename"
                    rules={[{ required: true, whitespace: true, message: 'Enter a file name' }]}
                    style={{ marginBottom: 0 }}
                >
                    <Input suffix=".csv" onPressEnter={() => form.submit()} />
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default ExportCsvModal;
